import Modal from "@via/components/modal";
import SelectComponent from "@via/components/select/select";
import FormInputComponent from "@via/components/inputs/form-input-component";
import FormTextAreaComponent from "@via/components/inputs/form-textarea-component";
import { SkeletonLoader } from "@via/components/loading/loading";
import { useSchedulerForm } from "@via/pages/runs/schedulers/hooks/useSchedulerForm";
import {
  buildSchedulerPayload,
  createEmptyOverwriteRow,
  INTERVAL_OPTIONS,
  SOURCE_METHOD_OPTIONS,
  type SchedulerFormValues,
} from "@via/pages/runs/schedulers/utils/helpers";
import { OverwriteRowCard } from "@via/pages/runs/schedulers/components/overwrite-row-card";
import { SampleTrackerSourceFields } from "@via/pages/runs/schedulers/components/sample-tracker-source-fields";
import { requiredValidation } from "@via/utils/form-validators";
import { ModalSize } from "@via/utils/constants";
import { classNames } from "@via/utils/helpers";
import type { JSX } from "react";
import type { RunScheduler } from "common";

export default function CreateSchedulerModal({
  open,
  setOpen,
  mode = "create",
  initialScheduler = null,
}: {
  open: boolean;
  setOpen: (open: boolean) => void;
  mode?: "create" | "edit";
  initialScheduler?: RunScheduler | null;
}): JSX.Element {
  const {
    control,
    register,
    setValue,
    getValues,
    trigger,
    clearErrors,
    setError,
    errors,

    overwriteFields,
    appendOverwriteRow,
    removeOverwriteRow,

    watchedSourceMethod,
    watchedTemplateRunId,
    watchedWatchSourcePath,
    watchedOverwriteRows,

    trackerView,
    setTrackerView,
    datasetTargetView,
    setDatasetTargetView,

    projectOptions,
    runOptions,
    amazonCredentialOptions,
    googleCredentialOptions,
    overwriteFieldOptions,
    vmetaCanvasOptions,
    overwriteValueTypeOptions,
    overwriteMethodOptions,

    isProjectsLoading,
    isRunsLoading,
    isLoadingConfig,
    isOverwriteFieldsLoading,
    isVmetaCanvasLoading,

    areOverwriteRowsValid,
    overwriteRowsValidationMessage,
    selectedCollectionIdForOverwrites,

    mongoUserId,

    createScheduler,
    isCreatePending,
    updateScheduler,
    isUpdatePending,

    setNotification,
    NOTIFICATION,

    loadTemplateConfig,
  } = useSchedulerForm(open, mode, initialScheduler);

  const handleConfirm = async () => {
    clearErrors();
    const values = getValues();

    const baseFields: (keyof SchedulerFormValues)[] = ["name", "projectId"];
    const conditionalFields: (keyof SchedulerFormValues)[] =
      values.sourceMethod === "watch-path"
        ? ["watchSourcePath"]
        : [
            "sampleTrackerId",
            "collectionId",
            "watchPathField",
            "statusField",
            "readyValue",
          ];
    const isValid = await trigger([...baseFields, ...conditionalFields]);

    if (
      !isValid ||
      (watchedOverwriteRows.length > 0 && !areOverwriteRowsValid)
    ) {
      const fieldLabels: Partial<Record<keyof SchedulerFormValues, string>> = {
        name: "Scheduler name",
        projectId: "Project",
        watchSourcePath: "Watch path (source)",
        sampleTrackerId: "Sample tracker source",
        collectionId: "Collection",
        watchPathField: "Source value field",
        statusField: "Status field",
        readyValue: "Ready value",
      };

      const requiredFieldNames = [...baseFields, ...conditionalFields];
      const missingFieldLabels = requiredFieldNames
        .map(f => {
          const msg = errors?.[f]?.message;
          return msg ? `${fieldLabels[f] ?? String(f)}: ${msg}` : null;
        })
        .filter(Boolean) as string[];

      if (watchedOverwriteRows.length > 0 && !areOverwriteRowsValid) {
        setError("overwriteRows", {
          message:
            overwriteRowsValidationMessage ||
            "Please fill all overwrite rows (Config parameter and value).",
        });
      }

      const details = [
        ...missingFieldLabels,
        watchedOverwriteRows.length > 0 && !areOverwriteRowsValid
          ? `Overwrite parameters: ${
              overwriteRowsValidationMessage ||
              "Please fill all overwrite rows (Config parameter and value)."
            }`
          : null,
      ]
        .filter(Boolean)
        .join(" · ");

      setNotification({
        id: "scheduler-create-validation-failed",
        message: "Please fill required fields",
        details,
        variant: NOTIFICATION.ERROR,
      });
      throw new Error("Scheduler create validation failed");
    }

    const payload = buildSchedulerPayload(values);

    if (mode === "edit") {
      if (!initialScheduler?.id) {
        throw new Error("Missing scheduler id");
      }
      await updateScheduler({
        id: initialScheduler.id,
        data: {
          name: payload.name,
          enabled: payload.enabled,
          config: payload,
        },
      });
    } else {
      await createScheduler(payload);
    }
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title={mode === "edit" ? "Edit Scheduler" : "Create Scheduler"}
      size={ModalSize.XLarge}
      confirmText={mode === "edit" ? "Save" : "Create"}
      isConfirmLoading={mode === "edit" ? isUpdatePending : isCreatePending}
      isConfirmDisabled={isLoadingConfig}
      onConfirm={handleConfirm}>
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="md:col-span-2">
            <FormInputComponent
              control={control}
              name="name"
              label="Scheduler name"
              placeholder="Enter scheduler name"
              showRequiredField
              validations={requiredValidation("Scheduler name")}
            />
          </div>
        </div>

        <div className="rounded-md border border-gray-200 bg-white p-4">
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-gray-900">
              Source detection
            </h3>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-900">
                Source method
              </label>
              <div className="mt-2">
                <span className="isolate inline-flex w-full rounded-md">
                  {SOURCE_METHOD_OPTIONS.map((opt, idx) => {
                    const isActive = watchedSourceMethod === opt.value;
                    return (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => setValue("sourceMethod", opt.value)}
                        className={classNames(
                          "relative inline-flex flex-1 select-none items-center justify-center px-3 py-2 text-sm font-semibold ring-1 ring-inset ring-gray-300 hover:bg-gray-50 focus:z-10",
                          idx === 0 ? "rounded-l-md" : "-ml-px",
                          idx === SOURCE_METHOD_OPTIONS.length - 1
                            ? "rounded-r-md"
                            : "",
                          isActive
                            ? "bg-indigo-600 text-white hover:bg-indigo-500"
                            : "bg-white text-gray-900"
                        )}>
                        {opt.label}
                      </button>
                    );
                  })}
                </span>
              </div>
            </div>

            {watchedSourceMethod === "sample-tracker" ? (
              <SampleTrackerSourceFields
                control={control}
                setValue={setValue}
                trackerView={trackerView}
                setTrackerView={setTrackerView}
                mongoUserId={mongoUserId}
              />
            ) : (
              <div className="md:col-span-2">
                <FormInputComponent
                  control={control}
                  name="watchSourcePath"
                  label="Watch path (source)"
                  placeholder="e.g. s3://bucket/path/to/incoming/ OR /home/path"
                  validations={requiredValidation("Watch path")}
                />

                {watchedWatchSourcePath?.trim().startsWith("s3://") ? (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-900">
                      S3 credentials (optional)
                    </label>
                    <div className="mt-2">
                      <SelectComponent
                        control={control}
                        name="amazonCredentialId"
                        options={amazonCredentialOptions}
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Choosing{" "}
                      <span className="font-medium">Account Default</span> will
                      not be saved as a credential id.
                    </p>
                  </div>
                ) : null}

                {watchedWatchSourcePath?.trim().startsWith("gs://") ? (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-900">
                      GCS credentials (optional)
                    </label>
                    <div className="mt-2">
                      <SelectComponent
                        control={control}
                        name="googleCredentialId"
                        options={googleCredentialOptions}
                      />
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Choosing{" "}
                      <span className="font-medium">Account Default</span> will
                      not be saved as a credential id.
                    </p>
                  </div>
                ) : null}
              </div>
            )}

            {watchedSourceMethod === "watch-path" ? (
              <div className="md:col-span-2">
                <FormInputComponent
                  control={control}
                  name="markerFileRelativePath"
                  label="Marker file (relative path, optional)"
                  placeholder="e.g. RTAComplete.txt or status/RTAComplete.txt"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Relative to the detected folder.
                </p>
              </div>
            ) : null}

            <div className="md:col-span-2 grid grid-cols-1 gap-4 md:grid-cols-3">
              <div>
                <FormInputComponent
                  control={control}
                  name="startDate"
                  label="Start date (optional)"
                  type="datetime-local"
                />
              </div>

              <div>
                <FormInputComponent
                  control={control}
                  name="endDate"
                  label="End date (optional)"
                  type="datetime-local"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900">
                  Interval
                </label>
                <div className="mt-2">
                  <SelectComponent
                    control={control}
                    name="intervalMinutes"
                    options={INTERVAL_OPTIONS}
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Minimum 15 minutes.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="rounded-md border border-gray-200 bg-white p-4">
          <div className="mb-4">
            <h3 className="text-sm font-semibold text-gray-900">
              Run Configuration
            </h3>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-gray-900">
                Submit runs to project
              </label>
              <div className="mt-2">
                {isProjectsLoading ? (
                  <SkeletonLoader count={1} />
                ) : (
                  <SelectComponent
                    control={control}
                    name="projectId"
                    rules={{ required: "Project is required" }}
                    options={projectOptions}
                    placeholder="Select a project"
                  />
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-900">
                Template run
              </label>
              <div className="mt-2 flex gap-2">
                <div className="flex-1">
                  <SelectComponent
                    control={control}
                    name="templateRunId"
                    options={runOptions}
                    placeholder="Select a run"
                    isLoading={isRunsLoading}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => void loadTemplateConfig()}
                  disabled={!watchedTemplateRunId || isLoadingConfig}
                  className="inline-flex items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 disabled:opacity-50">
                  Load
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-500">
                Load uses the run config download endpoint and fills JSON below.
              </p>
            </div>
          </div>

          <div className="mt-6">
            <label className="block text-sm font-medium text-gray-900">
              Run config JSON
            </label>
            <div className="mt-2">
              {isLoadingConfig ? (
                <div className="grid grid-cols-1 gap-2 py-2">
                  <SkeletonLoader count={6} />
                </div>
              ) : (
                <FormTextAreaComponent
                  control={control}
                  name="runConfigText"
                  rows={14}
                  spellCheck={false}
                  className="p-3 font-mono text-xs"
                />
              )}
            </div>
            <p className="mt-1 text-xs text-gray-500">
              You can edit this JSON manually before creating the scheduler.
            </p>
          </div>
        </div>

        <div className="rounded-md border border-gray-200 bg-white p-4">
          <div className="mb-4 flex items-start justify-between gap-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                Overwrite Parameters
              </h3>
              <p className="mt-1 text-xs text-gray-500">
                Optional. These values will overwrite fields in the Run config
                at execution time.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                const { id: _, ...row } =
                  createEmptyOverwriteRow(watchedSourceMethod);
                appendOverwriteRow(row);
              }}
              className="inline-flex items-center justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50">
              + Add
            </button>
          </div>

          {overwriteFields.length === 0 ? (
            <p className="text-sm text-gray-500">No overwrite parameters.</p>
          ) : (
            <div className="space-y-4">
              {overwriteFields.map((field, idx) => {
                const row = watchedOverwriteRows[idx];
                if (!row) return null;
                return (
                  <OverwriteRowCard
                    key={field.id}
                    idx={idx}
                    fieldId={field.id}
                    row={row}
                    sourceMethod={watchedSourceMethod}
                    selectedCollectionId={selectedCollectionIdForOverwrites}
                    control={control}
                    register={register}
                    setValue={setValue}
                    overwriteFieldOptions={overwriteFieldOptions}
                    overwriteValueTypeOptions={overwriteValueTypeOptions}
                    overwriteMethodOptions={overwriteMethodOptions}
                    amazonCredentialOptions={amazonCredentialOptions}
                    googleCredentialOptions={googleCredentialOptions}
                    vmetaCanvasOptions={vmetaCanvasOptions}
                    isOverwriteFieldsLoading={isOverwriteFieldsLoading}
                    isVmetaCanvasLoading={isVmetaCanvasLoading}
                    datasetTargetView={datasetTargetView}
                    setDatasetTargetView={setDatasetTargetView}
                    onRemove={() => removeOverwriteRow(idx)}
                  />
                );
              })}

              {errors.overwriteRows?.message && (
                <p className="text-sm text-red-600">
                  {errors.overwriteRows.message}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
